import { useCallback, useMemo } from 'react';
import {
	GROUP_BY_ASSIGNEE,
	GROUP_BY_CATEGORY,
	GROUP_BY_PRIORITY,
	GROUP_BY_STATUS,
} from '@atlassian/jira-business-constants';
import {
	CARD_APPEARANCE,
	type AdditionalCreateIssueContext,
	type InlineCreateProps,
} from '@atlassian/jira-business-issue-create';
import {
	type StatusCategoryKeys,
	statusCategoryForId,
	StatusCategoryIds,
} from '@atlassian/jira-common-constants/src/status-categories';
import { useIntl } from '@atlassian/jira-intl';
import { ISSUE_KEY_TYPE } from '@atlassian/jira-platform-field-config';
import { BOARD_FIELD_CATEGORY, NULL_GROUP_KEY } from '../../common/constants';
import type { BoardColumn, BoardIssueCategoryFieldOld } from '../../common/types';
import { useActiveWorkflowDetails, useBoardStore } from '../../services/board-store';
import type { CreatedBoardItem } from '../../services/create-issue';
import { transformFieldsOld } from '../../utils/board-field-transformer-old';
import { getIssueFromIssueFieldsOld } from '../../utils/get-issue-from-issue-fields-old';

type UseInlineCreate = {
	column: BoardColumn;
	onAddOptimisticIssue?: () => void;
	rankBeforeIssueId?: number;
};

/** Clean up when removing jwm.board-awesome-enhancements */
export const useInlineCreateProps = ({
	column,
	onAddOptimisticIssue,
	rankBeforeIssueId,
}: UseInlineCreate) => {
	const [
		{ categoryFieldId, issueFields },
		{ addIssue, removeIssue, removeIssueWithFields, updateIssue },
	] = useBoardStore();

	const statusCategoryId =
		column.groupBy === GROUP_BY_STATUS
			? // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				StatusCategoryIds[column.statusCategory as StatusCategoryKeys]
			: undefined;

	const [activeWorkflowDetails] = useActiveWorkflowDetails();
	const intl = useIntl();

	const fieldIdsToReturn = useMemo(
		() => [...Object.keys(issueFields ?? []), ISSUE_KEY_TYPE],
		[issueFields],
	);

	const allowedIssueTypeIds = useMemo(() => {
		if (!activeWorkflowDetails) {
			return undefined;
		}
		return activeWorkflowDetails.issueTypes.map((issueType) => issueType.issueTypeId);
	}, [activeWorkflowDetails]);

	const prepareIssueContext = useCallback(() => {
		const issueContext: AdditionalCreateIssueContext = {};

		if (rankBeforeIssueId) {
			issueContext.rankBefore = {
				issueId: String(rankBeforeIssueId),
				rank: '',
			};
		}

		if (column.groupKey === NULL_GROUP_KEY) {
			return issueContext;
		}

		switch (column.groupBy) {
			case GROUP_BY_ASSIGNEE:
				issueContext.assignee = {
					accountId: column.groupKey ?? '',
					avatarUrl: column.imageUrl ?? '',
					displayName: column.name ?? '',
				};
				break;
			case GROUP_BY_PRIORITY:
				issueContext.priority = {
					iconUrl: column.imageUrl ?? '',
					name: column.name ?? '',
					priorityId: column.groupKey ?? '',
				};
				break;
			case GROUP_BY_CATEGORY:
				issueContext.category = {
					color: column.color || '',
					optionId: column.groupKey ?? '',
					value: column.name ?? '',
				};
				break;
			case GROUP_BY_STATUS:
				issueContext.status = {
					name: column.name ?? '',
					statusCategoryId: statusCategoryId ?? 0,
					statusId: column.groupKey ?? '',
				};
				break;
			default:
				break;
		}

		return issueContext;
	}, [column, rankBeforeIssueId, statusCategoryId]);

	const handleSubmit: InlineCreateProps<CreatedBoardItem>['onSubmit'] = useCallback(
		async ({ optimisticIssuePayload, promise }) => {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const optimisticIssue: any = {
				assigneeKey: null,
				assigneeName: null,
				dueDate: null,
				id: optimisticIssuePayload.issueId,
				issueTypeId: optimisticIssuePayload.issueType.id,
				key: '',
				needsExtendedFieldsUpdate: false,
				statusId: 0,
				subtaskMetadata: {
					done: 0,
					total: 0,
				},
				summary: optimisticIssuePayload.summary,
				url: '',
			};

			if (optimisticIssuePayload.assignee) {
				optimisticIssue.assignee = {
					avatarUrl: optimisticIssuePayload.assignee.avatarUrl,
					displayName: optimisticIssuePayload.assignee.displayName,
				};
				optimisticIssue.assigneeKey = optimisticIssuePayload.assignee.accountId;
				optimisticIssue.assigneeName = optimisticIssuePayload.assignee.displayName;
			}

			if (optimisticIssuePayload.priority) {
				optimisticIssue.priorityIconUrl = optimisticIssuePayload.priority.iconUrl;
				optimisticIssue.priorityId = optimisticIssuePayload.priority.priorityId;
				optimisticIssue.priorityName = optimisticIssuePayload.priority.name;
			}

			if (optimisticIssuePayload.status) {
				optimisticIssue.statusCategory = statusCategoryForId(
					optimisticIssuePayload.status.statusCategoryId,
				);
				optimisticIssue.statusId = Number(optimisticIssuePayload.status.statusId);
				optimisticIssue.statusName = optimisticIssuePayload.status.name;
				optimisticIssue.untranslatedStatusName = optimisticIssuePayload.status.name;
			}

			let optimisticExtendedFields;

			if (optimisticIssuePayload.category && categoryFieldId) {
				const optimisticCategoryField: BoardIssueCategoryFieldOld = {
					fieldId: categoryFieldId,
					id: optimisticIssuePayload.category.optionId,
					name: column.groupName ?? '',
					type: BOARD_FIELD_CATEGORY,
					value: optimisticIssuePayload.category.value,
					color: optimisticIssuePayload.category.color ?? '',
				};

				optimisticExtendedFields = {
					fields: [optimisticCategoryField],
					fieldsByAlias: {
						category: optimisticCategoryField,
					},
				};
			}

			const optimisticRankBeforeIssueId = optimisticIssuePayload.rankBefore?.issueId
				? Number(optimisticIssuePayload.rankBefore?.issueId)
				: undefined;

			addIssue(optimisticIssue, optimisticExtendedFields, optimisticRankBeforeIssueId);

			onAddOptimisticIssue?.();

			let createdBoardItem: CreatedBoardItem | null | undefined;

			try {
				createdBoardItem = await promise;
			} catch (e) {
				// swallow error
			}

			if (createdBoardItem) {
				const extendedFields = transformFieldsOld([createdBoardItem], intl)[
					createdBoardItem.issueId
				];

				const issue = getIssueFromIssueFieldsOld(createdBoardItem.issueId, extendedFields.fields);

				updateIssue(optimisticIssue.id, issue, extendedFields);
			}

			removeIssue(optimisticIssue.id);
			removeIssueWithFields(optimisticIssue.id);
		},
		[
			addIssue,
			categoryFieldId,
			column.groupName,
			intl,
			onAddOptimisticIssue,
			removeIssue,
			removeIssueWithFields,
			updateIssue,
		],
	);

	return {
		allowedIssueTypeIds,
		appearance: CARD_APPEARANCE,
		fieldIdsToReturn,
		onPrepareIssueContext: prepareIssueContext,
		onSubmit: handleSubmit,
	};
};
